/**
 * Действия нативной сессии: вход, регистрация, выход, проверка себя.
 *
 * Никакой оптимистики: пока запрос летит, форма показывает лоадер и
 * заблокирована, стор меняется только в `onSuccess`, ошибка уходит тостером.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { getApiErrorMessage, getRefreshToken } from '@/shared/api';
import { useAuthStore } from '@/store/auth';

import {
  fetchMe,
  googleCallback,
  identify,
  login,
  logout,
  register,
  selectGoogleTenant,
  verifyMfa,
} from '../api/auth';
import { isMfaChallenge } from './types';
import type { GoogleCallbackResponse, NativeTokenResponse } from './types';

function applySession(response: NativeTokenResponse) {
  useAuthStore.getState().setNativeSession(response.user, {
    access_token: response.access_token,
    refresh_token: response.refresh_token,
  });
}

/** Первый шаг входа: компании, где заведена почта. */
export function useIdentify() {
  return useMutation({ mutationFn: identify });
}

export function useNativeLogin() {
  return useMutation({
    mutationFn: (payload: {
      tenant_slug: string;
      email: string;
      password: string;
    }) => login(payload),
    // При включённой 2FA токенов в ответе нет — сессию проставит проверка кода.
    onSuccess: (response) => {
      if (!isMfaChallenge(response)) {
        applySession(response);
      }
    },
  });
}

/** Второй фактор после пароля. */
export function useVerifyMfa() {
  return useMutation({ mutationFn: verifyMfa, onSuccess: applySession });
}

/** Выбор компании после входа через Google. */
export function useSelectGoogleTenant() {
  return useMutation({
    mutationFn: selectGoogleTenant,
    onSuccess: applySession,
  });
}

/**
 * Обмен code от Google на сессию. Это не мутация: страница возврата зовёт
 * обмен из эффекта при монтировании, а StrictMode в dev сразу переподписывает
 * наблюдателя мутации — тот отцепляется от запроса и результата не получает.
 */
export async function exchangeGoogleCode(params: {
  code: string;
  state: string;
}): Promise<GoogleCallbackResponse> {
  const response = await googleCallback(params);

  if (response.status === 'logged_in') {
    applySession(response);
  }

  return response;
}

export function useNativeRegister() {
  return useMutation({
    mutationFn: (payload: {
      email: string;
      password: string;
      first_name: string;
      last_name: string;
    }) => register(payload),
    onSuccess: applySession,
  });
}

export function useNativeLogout() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      const refreshToken = getRefreshToken();
      if (!refreshToken) {
        return;
      }
      try {
        await logout(refreshToken);
      } catch {
        // Токен мог уже протухнуть или быть отозван — локально выходим всё равно.
      }
    },
    onSettled: () => {
      useAuthStore.getState().logout();
      queryClient.clear();
    },
  });
}

/**
 * Подтверждает сессию на бэке и подтягивает актуальные роли. Гвард пускает по
 * персистнутому пользователю, а этот запрос уже уточняет, жив ли он.
 */
export function useNativeMe(enabled: boolean) {
  return useQuery({
    queryKey: ['auth', 'me'],
    queryFn: fetchMe,
    enabled,
    staleTime: 5 * 60 * 1000,
    retry: false,
  });
}

/** Текст ошибки входа/регистрации в терминах пользователя, а не бэка. */
export function getAuthErrorMessage(error: unknown, fallback: string): string {
  const message = getApiErrorMessage(error, fallback);

  if (/invalid credentials/i.test(message)) {
    return 'Неверная почта или пароль';
  }
  if (/already|занят|exists/i.test(message)) {
    return 'Пользователь с такой почтой уже зарегистрирован';
  }
  if (/tenant/i.test(message)) {
    return 'Тенант не найден — проверьте VITE_TENANT_SLUG в .env';
  }

  return message;
}
